import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Modal,
  Tab,
  Tabs,
  Alert,
} from "react-bootstrap";
import {
  FaGoogle,
  FaMicrosoft,
  FaYandex,
  FaImage,
  FaLink,
  FaSearch,
  FaCamera,
  FaUpload,
} from "react-icons/fa";
import { Box } from "./reverseImageStyle";
import { isInIframe } from "../../utils/is-in-iframe";
import { ToolsHeader } from "../components/ToolsHeader";

const ReverseImageSearch: React.FC = () => {
  const [activeTab, setActiveTab] = useState("upload");
  const [imageUrl, setImageUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [capturedImage, setCapturedImage] = useState("");
  const [selectedEngine, setSelectedEngine] = useState("google");
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const isValidImageUrl = (url: string) => {
    return url.match(/\.(jpeg|jpg|png|gif)$/i) !== null;
  };

  // useEffect(() => {
  //   const height = document.body.scrollHeight;
  //   window.parent.postMessage({ type: "setHeight", height }, "*");
  // }, [errorMessage, capturedImage, previewUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && ["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setErrorMessage("");
      // const height = document.body.scrollHeight;
      // window.parent.postMessage({ type: "setHeight", height }, "*");
    } else {
      setErrorMessage("Please select a valid image file (PNG, JPEG, or GIF).");
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setErrorMessage(
        "Could not access the camera. Please ensure you've granted camera permissions."
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext("2d");
      if (context) {
        canvasRef.current.width = videoRef.current.videoWidth;
        canvasRef.current.height = videoRef.current.videoHeight;
        context.drawImage(videoRef.current, 0, 0);
        const imageDataUrl = canvasRef.current.toDataURL("image/png");
        setCapturedImage(imageDataUrl);
        setShowCameraModal(false);
        setErrorMessage("");
        stopCamera();
        // const height = document.body.scrollHeight;
        // window.parent.postMessage({ type: "setHeight", height }, "*");
      }
    }
  };

  const uploadToR2 = async (file: File): Promise<string | null> => {
    try {
      setIsUploading(true);
      setErrorMessage("");

      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(
        "https://r2-upload-url.connect-767.workers.dev/create-upload-url",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        setErrorMessage(
          `Failed to upload to Cloudflare R2. (${res.status}) ${text}`
        );
        return null;
      }

      const data = (await res.json()) as { publicUrl?: string; error?: string };

      if (!data.publicUrl) {
        setErrorMessage(
          data.error || "Failed to get public URL from Cloudflare R2."
        );
        return null;
      }

      return data.publicUrl;
    } catch (err) {
      console.error("Upload error:", err);
      setErrorMessage("Upload failed. Please try again.");
      return null;
    } finally {
      setIsUploading(false);
    }
  };



  const handleSearch = async () => {
    setIsUploading(true);
    setErrorMessage("");
    let finalImageUrl = imageUrl;

    try {
      if (activeTab === "upload" && !selectedFile) {
        setErrorMessage("Please select an image to upload.");
        return;
      }
      if (activeTab === "capture" && !capturedImage) {
        setErrorMessage("Please capture an image.");
        return;
      }
      if (activeTab === "url" && (!imageUrl || !isValidImageUrl(imageUrl))) {
        setErrorMessage(
          "Please enter a valid image URL (e.g., ending in .jpg, .png)."
        );
        return;
      }
      if (activeTab === "keyword" && !keyword.trim()) {
        setErrorMessage("Please enter a search keyword.");
        return;
      }

      if (activeTab === "upload" && selectedFile) {
        const uploadedUrl = await uploadToR2(selectedFile);
        if (!uploadedUrl) return;
        finalImageUrl = uploadedUrl;
      } else if (activeTab === "capture" && capturedImage) {
        const blob = await fetch(capturedImage).then((res) => res.blob());
        const file = new File([blob], "captured.png", { type: "image/png" });
        const uploadedUrl = await uploadToR2(file);
        if (!uploadedUrl) return;
        finalImageUrl = uploadedUrl;
      }


      let searchUrl = "";
      if (selectedEngine === "google") {
        if (finalImageUrl) {
          searchUrl = `https://lens.google.com/uploadbyurl?url=${encodeURIComponent(
            finalImageUrl
          )}`;
        } else if (activeTab === "keyword") {
          searchUrl = `https://www.google.com/search?tbm=isch&q=${encodeURIComponent(
            keyword
          )}`;
        }
      } else if (selectedEngine === "bing") {
        if (finalImageUrl) {
          searchUrl = `https://www.bing.com/images/search?view=detailv2&iss=sbi&form=SBIVSP&sbisrc=UrlPaste&q=imgurl:${encodeURIComponent(
            finalImageUrl
          )}`;
        } else {
          searchUrl = `https://www.bing.com/images/search?q=${encodeURIComponent(
            keyword
          )}`;
        }
      } else if (selectedEngine === "yandex") {
        if (finalImageUrl) {
          searchUrl = `https://yandex.com/images/search?source=collections&url=${encodeURIComponent(
            finalImageUrl
          )}&rpt=imageview`;
        } else {
          searchUrl = `https://yandex.com/images/search?text=${encodeURIComponent(
            keyword
          )}`;
        }
      } else if (selectedEngine === "baidu") {
        if (finalImageUrl) {
          searchUrl = `https://graph.baidu.com/details?isfromtusoupc=${encodeURIComponent(
            finalImageUrl
          )}`;
        } else {
          searchUrl = `https://image.baidu.com/search/index?tn=baiduimage&word=${encodeURIComponent(
            keyword
          )}`;
        }
      }

      if (searchUrl) {
        // const height = document.body.scrollHeight;
        // window.parent.postMessage({ type: "setHeight", height }, "*");
        setTimeout(() => {
          window.open(searchUrl, "_blank");
        }, 200);
      } else {
        setErrorMessage("Unable to generate search URL. Please try again.");
      }
    } catch (error) {
      console.error("Search error:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
  if (!isInIframe) return;

  const updateHeight = () => {
    const height = document.documentElement.getBoundingClientRect().height;
    window.parent.postMessage(
      { type: "setHeight", height },
      "*"
    );
  };

  const resizeObserver = new ResizeObserver(updateHeight);

  resizeObserver.observe(document.documentElement);

  window.addEventListener("load", updateHeight);

  return () => {
    resizeObserver.disconnect();
    window.removeEventListener("load", updateHeight);
  };
}, []);

  return (
    <Box>
      <Container
        className={`reverse-image-container ${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"
          }`}
        style={{ padding: "0px" }}
      >
        <Card className="reverse-image-card glass-card">
          <Card.Body className="p-4">
            {!isInIframe && (
              <ToolsHeader
                title="Reverse Image Search"
                description="Quickly find similar images or sources online using powerful reverse image search tools."
              />
            )}

            {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

            <Tabs
              activeKey={activeTab}
              onSelect={(k) => {
                setActiveTab(k || "upload");
                // const height = document.body.scrollHeight;
                // window.parent.postMessage({ type: "setHeight", height }, "*");
              }}
              className="mb-4 custom-tabs"
              fill
            >
              <Tab
                eventKey="upload"
                title={
                  <span>
                    <FaUpload className="me-2" /> Upload Image
                  </span>
                }
              >
                <div className="mt-3 text-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="d-none"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="upload-label">
                    <FaUpload className="me-2" />
                    {previewUrl ? "Change Image" : "Choose Image"}
                  </label>
                  {previewUrl && (
                    <div className="mt-3">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="upload-preview"
                        onLoad={() => {
                          if (!isInIframe) return;
                          const height = document.documentElement.scrollHeight;
                          window.parent.postMessage(
                            { type: "setHeight", height },
                            "*"
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              </Tab>

              <Tab
                eventKey="keyword"
                title={
                  <span>
                    <FaSearch className="me-2" /> Keyword
                  </span>
                }
              >
                <Form.Group className="mt-3">
                  <Form.Control
                    type="text"
                    placeholder="Enter search keyword"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    className="modern-input"
                  />
                </Form.Group>
              </Tab>

              <Tab
                eventKey="capture"
                title={
                  <span>
                    <FaCamera className="me-2" /> Capture
                  </span>
                }
              >
                <div className="mt-3 text-center">
                  {capturedImage ? (
                    <>
                      <img
                        src={capturedImage}
                        alt="Captured"
                        className="capture-preview"
                      />
                      <Button
                        variant="outline-primary"
                        className="mt-3"
                        onClick={() => setShowCameraModal(true)}
                      >
                        Retake Photo
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => setShowCameraModal(true)}
                    >
                      <FaCamera className="me-2" /> Open Camera
                    </Button>
                  )}
                </div>
              </Tab>

              <Tab
                eventKey="url"
                title={
                  <span>
                    <FaLink className="me-2" /> Image URL
                  </span>
                }
              >
                <Form.Group className="mt-3">
                  <Form.Control
                    type="text"
                    placeholder="Paste image URL here"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    className="modern-input"
                  />
                </Form.Group>
              </Tab>
            </Tabs>

            <div className="search-engines mt-4">
              <h5 className="text-center mb-3">Search With:</h5>
              <div className="d-flex justify-content-center flex-wrap gap-3">
                {[
                  { id: "google", icon: <FaGoogle />, name: "Google" },
                  { id: "bing", icon: <FaMicrosoft />, name: "Bing" },
                  { id: "yandex", icon: <FaYandex />, name: "Yandex" },
                  { id: "baidu", icon: <FaImage />, name: "Baidu" },
                ].map((engine) => (
                  <Button
                    key={engine.id}
                    variant={
                      selectedEngine === engine.id
                        ? "primary"
                        : "outline-primary"
                    }
                    className={`engine-btn ${selectedEngine === engine.id ? "active" : ""
                      }`}
                    onClick={() => setSelectedEngine(engine.id)}
                  >
                    <div className="engine-icon">{engine.icon}</div>
                    <div className="engine-name">{engine.name}</div>
                  </Button>
                ))}
              </div>
            </div>

            <Button
              variant="success"
              size="lg"
              className="w-100 mt-4 search-btn"
              onClick={handleSearch}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Search Now"}
            </Button>
          </Card.Body>
        </Card>

        <Modal
          show={showCameraModal}
          onHide={() => {
            setShowCameraModal(false);
            stopCamera();
          }}
          onEntered={startCamera}
        >
          <Modal.Header closeButton>
            <Modal.Title>Take a Photo</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="camera-view"
              width="100%"
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button variant="primary" onClick={captureImage}>
              <FaCamera className="me-2" /> Capture
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Box>
  );
};

export default ReverseImageSearch;
